import {
  AlertType, AlertTypeRule, PreviewOptions, Sensitivity, SensitivityNotchLabels, SeveritySensitivityConfig
} from '../../../models';

export const DEFAULT_NOTCH_LABELS: SensitivityNotchLabels = {
  sensitive: 'askAi.severitySensitivity.notchSensitive',
  balanced: 'askAi.severitySensitivity.notchBalanced',
  quiet: 'askAi.severitySensitivity.notchQuiet',
};

// Display order (calmest to most alert-prone).
export const SENSITIVITY_NOTCHES: Sensitivity[] = ['quiet', 'balanced', 'sensitive'];

// Fixed, alert-type-independent — the notch is a display filter over severity (info/warning/
// critical), never a change to what triggers a signal (see AskAiState.visibleSignals, the actual
// filter these describe). Same three lines on every card, unlike the old per-notch amount copy.
// Kept free of the severity badge names (critical/warning/info) by design — see NOTCH_TOOLTIPS,
// the only place those appear on this screen.
export const NOTCH_DESCRIPTIONS: Record<Sensitivity, string> = {
  quiet: 'askAi.severitySensitivity.notchDescriptionQuiet',
  balanced: 'askAi.severitySensitivity.notchDescriptionBalanced',
  sensitive: 'askAi.severitySensitivity.notchDescriptionSensitive',
};

// One-sentence hover detail per notch — the only place info/warning/critical are named on this
// screen (the short NOTCH_DESCRIPTIONS above deliberately avoid them).
export const NOTCH_TOOLTIPS: Record<Sensitivity, string> = {
  quiet: 'askAi.severitySensitivity.notchTooltipQuiet',
  balanced: 'askAi.severitySensitivity.notchTooltipBalanced',
  sensitive: 'askAi.severitySensitivity.notchTooltipSensitive',
};

export function notchLabel(alertType: AlertType, notch: Sensitivity): string {
  return (alertType.notchLabels || DEFAULT_NOTCH_LABELS)[notch];
}

// '%' and single-letter units (h, m, s) attach directly to the number ("40%", "45h"); word units
// get a space ("4 events", "6 entries"). Rounds to one decimal place — callers pass hand-authored
// thresholds (already whole numbers, unaffected) as well as raw ratios off the live signals
// endpoint (e.g. entry.ratio), which can arrive with long floating-point tails.
export function formatThresholdAmount(value: number, unit: string): string {
  const rounded = Math.round(value * 10) / 10;
  return unit === '%' || unit.length === 1 ? `${rounded}${unit}` : `${rounded} ${unit}`;
}

// Formats decimal hours as "8h 09m" (or "0m"/"<1m") — the "xh ym" shape unusual_activity/
// manual_time/mobile_time's `ratio` field actually arrives in from the backend (see
// parseRatioValue in ask-ai-mock-data.ts), unlike formatThresholdAmount's rounded-decimal "8.2h"
// above, which loses minute-level precision ("8h 9m" and "8h 6m" both round to "8.2h"). Handles
// negative input (signed "-Xh Ym") for the threshold-delta case; `ratio` itself is never negative.
// Mirrors formatDurationFromHours (ai-response.types.ts) — kept as its own small copy here rather
// than importing across feature folders for a five-line function.
export function formatDurationAmount(hours: number): string {
  if (!Number.isFinite(hours) || hours === 0) return '0m';
  const sign = hours < 0 ? '-' : '';
  const totalMinutes = Math.round(Math.abs(hours) * 60);
  if (totalMinutes < 1) return `${sign}<1m`;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${sign}${m}m`;
  const mm = m < 10 ? `0${m}m` : `${m}m`;
  return `${sign}${h}h ${mm}`;
}

// Heavy hours/manual time added/mobile time added pick whole hours only — no decimals, no minute
// granularity (unlike unusual_activity, which stays minute-precise down to its 0m default).
const WHOLE_HOURS_ONLY = new Set(['heavy_hours', 'manual_time', 'mobile_time']);

export function isWholeHoursOnly(alertType: AlertType): boolean {
  return WHOLE_HOURS_ONLY.has(alertType.key);
}

// Duration units ('h') render as "3h 09m"/"9m"/"<1m" via formatDurationAmount, never a rounded
// decimal like "3.15h" — percentage stays on formatThresholdAmount's one-decimal "%" rounding.
// Whole-hour values drop the redundant " 00m" (e.g. "8h" not "8h 00m") for this file's own
// condition-sentence/threshold-field copy specifically — formatDurationAmount itself is shared with
// other Needs You/threshold-chart rendering out of Settings' scope, so it's left untouched.
// wholeHoursOnly forces a plain integer-hour amount ("8h") for the three signals above, since they
// never carry minutes at all.
export function formatAmount(value: number, unit: string, wholeHoursOnly = false): string {
  if (unit === '%') return formatThresholdAmount(value, unit);
  if (wholeHoursOnly) return `${Math.round(value)}h`;
  const formatted = formatDurationAmount(value);
  return formatted.endsWith(' 00m') ? formatted.slice(0, -4) : formatted;
}

// The single per-day condition sentence — same for every notch (see NOTCH_DESCRIPTIONS), since the
// threshold no longer varies by sensitivity. Replaces the old buildPreviews per-notch "well over X"
// wording below, which wrongly implied sensitivity changes what triggers a signal — it only filters
// what's shown (AskAiState.visibleSignals). `directionLabel` is pre-translated by the caller (same
// pattern as SeveritySensitivityComponent.caption()) since this file has no TranslateService.
export function dailyCondition(alertType: AlertType, severity: SeveritySensitivityConfig, directionLabel: string): string {
  const amount = formatAmount(severity.threshold, severity.unit, isWholeHoursOnly(alertType));
  return `${alertType.subject} ${alertType.subjectVerb} ${directionLabel} ${amount}.`;
}

export interface ThresholdLimits {
  min: number;
  max: number;
}

// Step 3 input constraints: percentage 1-90 (91-100 rejected), duration 1-minute-equivalent to 24h
// (0 and negatives rejected) — except unusual_activity, whose threshold may be exactly 0 ("flag any
// unusual activity at all" is the deliberate per-day default for that signal), and heavy_hours/
// manual_time/mobile_time, whose minimum is a whole 1h since they don't accept minute-level values.
export function thresholdLimits(alertType: AlertType): ThresholdLimits {
  if (alertType.severity.unit === '%') return { min: 1, max: 90 };
  if (isWholeHoursOnly(alertType)) return { min: 1, max: 24 };
  return { min: alertType.key === 'unusual_activity' ? 0 : 1 / 60, max: 24 };
}

// Validation message key for the current draft value, or null when it's within thresholdLimits.
export function thresholdError(alertType: AlertType, value: string): string | null {
  const num = Number(value);
  if (value.trim() === '' || !Number.isFinite(num)) return 'askAi.severitySensitivity.thresholdInvalid';
  if (isWholeHoursOnly(alertType) && !Number.isInteger(num)) return 'askAi.severitySensitivity.thresholdMustBeWholeHours';
  const { min, max } = thresholdLimits(alertType);
  if (num < min) return 'askAi.severitySensitivity.thresholdTooLow';
  if (num > max) return 'askAi.severitySensitivity.thresholdTooHigh';
  return null;
}

// The parenthetical/short-caption delta shown by buildRules below.
function deltaFormatter(unit: string, deltaWord: string | undefined, deltaHasOver: boolean): (value: number) => string {
  return (value: number) => {
    if (deltaWord) return deltaHasOver ? `${value}+ ${deltaWord} over` : `${value}+ ${deltaWord}`;
    if (unit === '%' || unit.length === 1) return `${value}${unit}+`;
    return deltaHasOver ? `${value}+ over` : `${value}+`;
  };
}

// Short per-notch caption ("Anything over 40%", "About 10+ points over", "Only 20+ points over") —
// populates AlertType.rules, a field mapAlertTypeResponses always fills but nothing renders today
// (see ask-ai-mock-data.ts's ALERT_TYPE_COPY, none of which hand-authors `rules`).
export function buildRules(severity: SeveritySensitivityConfig, opts: PreviewOptions): AlertTypeRule {
  const { threshold, unit, x, y } = severity;
  const { deltaWord, deltaHasOver = true } = opts;
  const amount = formatThresholdAmount(threshold, unit);
  const delta = deltaFormatter(unit, deltaWord, deltaHasOver);

  return {
    sensitive: `Anything over ${amount}`,
    balanced: `About ${delta(x)}`,
    quiet: `Only ${delta(y)}`,
  };
}

